import { useCallback, useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { NavItem } from 'components/navbar'
import { storeCategoryPath } from 'lib/urls'
import { Guides, SubNav } from './dropdown-content'
import css from '../navbar.scss'
import { track, useTracking } from 'lib/analytics'

const CategoryDropdown = ({ category, isActiveItem, backdrop }) => {
  const tracker = useTracking()
  const [open, setOpen] = useState(false)
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)

  const { title, urlSlug, children, guides } = category
  const visibleChildren = children && children.filter((c) => !c.hidden)
  const hasChildren = visibleChildren && visibleChildren.length > 0
  const hasGuides = guides && guides.length > 0

  const closePopup = () => {
    clearTimeout(openTimerRef.current)
    closeTimerRef.current = setTimeout(() => setOpen(false), 100)
  }

  const openPopup = () => {
    clearTimeout(closeTimerRef.current)
    openTimerRef.current = setTimeout(() => setOpen(true), 250)
  }

  useEffect(() => {
    return () => {
      clearTimeout(openTimerRef.current)
      clearTimeout(closeTimerRef.current)
    }
  }, [])

  const trackCategoryClick = (category) => {
    const leafCategory = !hasChildren
    tracker.trackEvent({
      event: track.storeCategoryClicked,
      storeCategoryName: category.title,
      isLeafCategory: leafCategory,
      storeCategorySlug: category.urlSlug,
      eventLocation: track.EventLocation.SHOP_MINUS_FLYOUT_NAV,
    })
  }

  const handleCategoryClick = useCallback(() => {
    trackCategoryClick(category)
    closePopup()
  }, [category, trackCategoryClick, closePopup])

  return (
    <>
      <NavItem
        router
        key={title}
        onClick={handleCategoryClick}
        onMouseLeave={closePopup}
        onMouseEnter={openPopup}
        className={classNames(css.catalogNavItem, {
          [css.catalogNavItemActive]: isActiveItem,
        })}
        to={{
          state: { slugSet: [urlSlug] },
          pathname: storeCategoryPath(urlSlug),
        }}
      >
        {title}
      </NavItem>

      {open && (hasChildren || hasGuides) && (
        <>
          <div className={`${css.categoryDropdown}`}>
            <div className="container">
              <div
                className={`fadeIn ${css.categoryDropdownContent}`}
                onMouseEnter={openPopup}
                onMouseLeave={closePopup}
              >
                {hasChildren && (
                  <SubNav
                    items={visibleChildren}
                    onCategoryClick={trackCategoryClick}
                  />
                )}
                {hasGuides && <Guides title={title} guides={guides} />}
              </div>
            </div>
          </div>
          {backdrop}
        </>
      )}
    </>
  )
}

CategoryDropdown.propTypes = {
  category: PropTypes.shape({
    title: PropTypes.string.isRequired,
    urlSlug: PropTypes.string.isRequired,
    children: PropTypes.array,
    guides: PropTypes.array,
  }).isRequired,
  isActiveItem: PropTypes.bool.isRequired,
  backdrop: PropTypes.element.isRequired,
}

export default CategoryDropdown
